import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PawPrint, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/api";
import { subscribeToCheckoutVisibility } from "@/lib/checkoutOverlay";

const PUBLIC_PATHS_EXACT = ["/", "/cases", "/sanctuary", "/success-stories", "/donate", "/terms", "/privacy", "/refund-policy", "/contact", "/transparency"];

function isPublicPath(pathname) {
  return PUBLIC_PATHS_EXACT.includes(pathname) || pathname.startsWith("/case/");
}

function getSessionId() {
  let id = localStorage.getItem("amtm_chat_session_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("amtm_chat_session_id", id);
  }
  return id;
}

const GREETING = "Hi! I'm AMTM's assistant. Ask me anything about our rescues, donations, or sanctuary.";

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(getSessionId());

  useEffect(
    () =>
      subscribeToCheckoutVisibility(
        () => {
          setCheckoutOpen(true);
          setOpen(false);
        },
        () => setCheckoutOpen(false)
      ),
    []
  );

  useEffect(() => {
    if (hasOpenedOnce) return;
    const showTimer = setTimeout(() => setShowHint(true), 2500);
    return () => clearTimeout(showTimer);
  }, [hasOpenedOnce]);

  useEffect(() => {
    if (!showHint) return;
    // Auto-dismiss after a few seconds so it doesn't sit on top of page
    // content indefinitely while a visitor scrolls (especially on mobile,
    // where it covers a much bigger share of the screen).
    const hideTimer = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(hideTimer);
  }, [showHint]);

  useEffect(() => {
    if (!open || messages.length > 0) return;
    fetch(`${BACKEND_URL}/api/chat/history/${sessionIdRef.current}`)
      .then((r) => r.json())
      .then((data) => {
        setMessages(data.items?.length ? data.items : [{ role: "assistant", content: GREETING }]);
      })
      .catch(() => setMessages([{ role: "assistant", content: GREETING }]));
  }, [open, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  if (!isPublicPath(location.pathname) || checkoutOpen) return null;

  const handleToggle = () => {
    setOpen((v) => !v);
    setHasOpenedOnce(true);
    setShowHint(false);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") continue;
          try {
            const { content } = JSON.parse(chunk);
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + content };
              return next;
            });
          } catch {
            // ignore malformed chunk
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && showHint && (
        <div
          data-testid="chat-widget-hint"
          className="fixed bottom-44 sm:bottom-24 right-6 z-50 max-w-[210px] bg-amtm-text text-amtm-bg text-sm font-medium px-4 py-2.5 rounded-2xl rounded-br-sm shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          Have a question? Chat with us! 🐾
          <button
            type="button"
            onClick={() => setShowHint(false)}
            data-testid="chat-widget-hint-dismiss"
            className="absolute -top-2 -right-2 bg-amtm-surface border border-amtm-border rounded-full p-1 hover:bg-amtm-bg transition-colors"
          >
            <X className="h-3 w-3 text-amtm-textMuted" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={handleToggle}
        data-testid="chat-widget-toggle-button"
        aria-label="Chat with AMTM Assistant"
        className="fixed bottom-24 sm:bottom-6 right-6 z-50 h-16 w-16 rounded-full bg-gradient-to-br from-amtm-teal to-[#0a6b61] text-white shadow-xl flex items-center justify-center hover:scale-110 hover:shadow-2xl active:scale-95 transition-all duration-200"
      >
        {!open && !hasOpenedOnce && <span className="absolute inset-0 rounded-full bg-amtm-teal/60 animate-ping" />}
        <span className="relative transition-transform duration-200">
          {open ? <X className="h-6 w-6" /> : <PawPrint className="h-7 w-7" />}
        </span>
        {!open && !hasOpenedOnce && (
          <span
            className="absolute top-0.5 right-0.5 h-3.5 w-3.5 rounded-full bg-amtm-teal border-2 border-amtm-bg"
            data-testid="chat-widget-notification-dot"
          />
        )}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-44 sm:bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-amtm-surface border border-amtm-border rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          <div className="bg-gradient-to-r from-amtm-teal to-[#0a6b61] text-white px-5 py-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <PawPrint className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display text-lg leading-tight">AMTM Assistant</p>
                <p className="text-xs text-white/70 leading-tight">Ask me anything ❤</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close-button"
              className="hover:bg-white/15 rounded-full p-1.5 transition-colors shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-widget-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`rounded-2xl px-3.5 py-2 text-sm max-w-[85%] whitespace-pre-wrap ${
                    m.role === "user" ? "bg-amtm-teal text-amtm-text" : "bg-amtm-teal/10 text-amtm-text border border-amtm-teal/20"
                  }`}
                  data-testid={`chat-message-${m.role}-${i}`}
                >
                  {m.content || (sending && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-amtm-border p-3 flex items-center gap-2 shrink-0">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Ask about AMTM..."
              data-testid="chat-widget-input"
              className="flex-1 h-10 rounded-full border border-amtm-border bg-amtm-bg px-4 text-sm outline-none focus:border-amtm-teal transition-colors"
            />
            <Button
              size="icon"
              onClick={handleSend}
              disabled={sending || !input.trim()}
              data-testid="chat-widget-send-button"
              className="h-10 w-10 rounded-full bg-amtm-teal text-white hover:bg-amtm-tealHover shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
